// Centralized Contact Information
// These are the defaults. The live values come from the API (Admin → Settings → Business and contact /
// Support hours) and are applied over this object by configs/siteConfig.js. Components that show them call
// useSiteConfig() so they re-render when the live values arrive.

export const contactInfo = {
  // Company Details
  companyName: 'Projects Kart',
  tagline: 'Ready-made projects with source code',

  // Owner Information
  owner: {
    name: 'Nitish Kumar',
    title: 'Founder & CEO',
    website: 'devhq.in',
    websiteUrl: 'https://devhq.in',
  },

  // Contact Details
  // TODO: no mailbox exists yet. Fill these in once one does; pages hide
  // email details while they are empty.
  email: {
    support: '',
    info: '',
    privacy: '',
    legal: '',
    projects: '',
  },

  phone: {
    primary: '+91 7019846664',
    display: '+91 70198 46664',
  },

  // WhatsApp number in international format, digits only (used for wa.me links)
  whatsapp: {
    number: '917019846664',
    display: '+91 70198 46664',
  },

  // Address
  address: {
    city: 'Bangalore',
    state: 'Karnataka',
    country: 'India',
  },

  // Business Hours
  businessHours: {
    days: 'Monday - Friday',
    time: '9:00 AM - 6:00 PM IST',
    timezone: 'IST',
    // Machine-readable version of the hours above (24h clock, Asia/Kolkata; 0 = Sunday)
    ianaTimezone: 'Asia/Kolkata',
    openHour: 9,
    closeHour: 18,
    weekdays: [1, 2, 3, 4, 5],
  },
};

// Helper function to get the (city-level) address
export const getFullAddress = () => {
  const { city, state, country } = contactInfo.address;
  return `${city}, ${state}, ${country}`;
};

// Helper function to get formatted business hours
export const getBusinessHours = () => {
  return `${contactInfo.businessHours.days}: ${contactInfo.businessHours.time}`;
};

// "Monday–Friday, 9:00 AM–6:00 PM IST" (en dashes, for compact lines)
export const getHoursLine = () => {
  const { days, time } = contactInfo.businessHours;
  return `${days.replace(' - ', '–')}, ${time.replace(' - ', '–')}`;
};

// "Monday to Friday, 9:00 AM to 6:00 PM IST" (for sentences)
export const getHoursSentence = () => {
  const { days, time } = contactInfo.businessHours;
  return `${days.replace(' - ', ' to ')}, ${time.replace(' - ', ' to ')}`;
};

// Helper function to build a WhatsApp chat link with a pre-filled message
export const getWhatsAppUrl = (text = '') => {
  const base = `https://wa.me/${contactInfo.whatsapp.number}`;
  return text ? `${base}?text=${encodeURIComponent(text)}` : base;
};

const WEEKDAY_NAMES = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

export const formatHour = hour => {
  const suffix = hour % 24 >= 12 ? 'PM' : 'AM';
  return `${hour % 12 || 12}:00 ${suffix}`;
};

// Weekdays in display order, Monday first (0 = Sunday).
const weekOrder = day => (day === 0 ? 7 : day);

// [1,2,3,4,5] -> "Monday - Friday", [1,3,5] -> "Monday, Wednesday, Friday", all seven -> "Every day".
export const describeWeekdays = weekdays => {
  const days = [...new Set(weekdays)].sort((a, b) => weekOrder(a) - weekOrder(b));
  if (days.length === 7) return 'Every day';
  if (days.length === 0) return '';
  const consecutive = days.every((day, i) => i === 0 || weekOrder(day) === weekOrder(days[i - 1]) + 1);
  if (consecutive && days.length > 2) {
    return `${WEEKDAY_NAMES[days[0]]} - ${WEEKDAY_NAMES[days[days.length - 1]]}`;
  }
  return days.map(day => WEEKDAY_NAMES[day]).join(', ');
};

// "9:00 AM - 6:00 PM IST"
export const describeHours = (openHour, closeHour, timezone = 'IST') =>
  `${formatHour(openHour)} - ${formatHour(closeHour)} ${timezone}`;

// Helper function to tell whether support is open right now, in the business timezone
export const getSupportStatus = (now = new Date()) => {
  const { ianaTimezone, openHour, closeHour, weekdays, timezone } =
    contactInfo.businessHours;
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: ianaTimezone,
    weekday: 'long',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(now);
  const part = type => parts.find(p => p.type === type)?.value;
  const day = WEEKDAY_NAMES.indexOf(part('weekday'));
  const minutes = Number(part('hour')) * 60 + Number(part('minute'));

  const isWorkday = weekdays.includes(day);
  if (isWorkday && minutes >= openHour * 60 && minutes < closeHour * 60) {
    return { open: true, detail: `Until ${formatHour(closeHour)} ${timezone}` };
  }

  // Find the next opening: later today, tomorrow, or a named weekday.
  let offset = isWorkday && minutes < openHour * 60 ? 0 : 1;
  while (!weekdays.includes((day + offset) % 7)) offset += 1;
  const when =
    offset === 0
      ? 'today'
      : offset === 1
        ? 'tomorrow'
        : WEEKDAY_NAMES[(day + offset) % 7];
  return {
    open: false,
    detail: `Opens ${when} at ${formatHour(openHour)} ${timezone}`,
  };
};

export default contactInfo;
